import React, { useState} from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Box,
    Typography,
    Collapse,
    IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess, FilterList } from '@mui/icons-material';
import { Organization } from './types';

interface OrganizationLocationFilterProps {
    organizations: Organization[];
    selectedOrganizations: number[];
    selectedLocations: number[];
    onOrganizationChange: (selectedOrgIds: number[]) => void;
    onLocationChange: (selectedLocationIds: number[]) => void;
    dataTestId?: string;
    placeholderText?: string;
    controlHeight?: number;
}

const OrganizationLocationFilter: React.FC<OrganizationLocationFilterProps> = ({
    organizations,
    selectedOrganizations,
    selectedLocations,
    onOrganizationChange,
    onLocationChange,
    dataTestId = "org-location-filter",
    placeholderText = "Filter Locations",
    controlHeight = 32,
}) => {
    const [open, setOpen] = useState(false);
    const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set());
    const controlTextSx = { fontSize: '0.76rem', fontWeight: 500 };

    // Check if all organizations are selected
    const isAllOrgsSelected = selectedOrganizations.length === organizations.length && organizations.length > 0;
    
    // Check if some (but not all) locations for a specific org are selected
    const areSomeLocationsSelected = (orgId: number) => {
        const org = organizations.find(o => o.orgId === orgId);
        if (!org || !org.locations || org.locations.length === 0) return false;
        
        const orgLocationIds = org.locations.map(loc => loc.locationId);
        const hasSelected = orgLocationIds.some(locId => selectedLocations.includes(locId));
        const hasUnselected = orgLocationIds.some(locId => !selectedLocations.includes(locId));
        
        return hasSelected && hasUnselected;
    };

    const handleToggleOrg = (orgId: number) => {
        const newExpanded = new Set(expandedOrgs);
        if (newExpanded.has(orgId)) {
            newExpanded.delete(orgId);
        } else {
            newExpanded.add(orgId);
        }
        setExpandedOrgs(newExpanded);
    };

    const handleSelectAll = () => {
        if (isAllOrgsSelected && selectedLocations.length === 0) {
            // Deselect all
            onOrganizationChange([]);
            onLocationChange([]);
        } else {
            // Select all organizations
            const allOrgIds = organizations.map(org => org.orgId);
            onOrganizationChange(allOrgIds);
            onLocationChange([]);
        }
    };

    const handleOrgSelect = (orgId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        
        const isSelected = selectedOrganizations.includes(orgId);
        let newSelectedOrgs: number[];
        
        if (isSelected) {
            // Deselect organization
            newSelectedOrgs = selectedOrganizations.filter(id => id !== orgId);
        } else {
            // Select organization
            newSelectedOrgs = [...selectedOrganizations, orgId];
        }
        
        // Clear location selections for this org when selecting/deselecting org
        const org = organizations.find(o => o.orgId === orgId);
        if (org && org.locations) {
            const orgLocationIds = org.locations.map(loc => loc.locationId);
            const newSelectedLocations = selectedLocations.filter(
                locId => !orgLocationIds.includes(locId)
            );
            onLocationChange(newSelectedLocations);
        }
        
        onOrganizationChange(newSelectedOrgs);
    };

    const handleLocationSelect = (orgId: number, locationId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        
        const isSelected = selectedLocations.includes(locationId);
        let newSelectedLocations: number[];
        
        if (isSelected) {
            // Deselect location
            newSelectedLocations = selectedLocations.filter(id => id !== locationId);
        } else {
            // Select location
            newSelectedLocations = [...selectedLocations, locationId];
        }
        
        // Deselect parent organization when selecting/deselecting location
        const newSelectedOrgs = selectedOrganizations.filter(id => id !== orgId);
        onOrganizationChange(newSelectedOrgs);
        onLocationChange(newSelectedLocations);
    };

    const renderValue = () => {
        const totalSelected = selectedOrganizations.length + selectedLocations.length;
        
        if (totalSelected === 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList sx={{ fontSize: 16, color: '#d97706' }} />
                    <Typography variant="body2" color="#4b5563" sx={controlTextSx}>
                        {placeholderText}
                    </Typography>
                </Box>
            );
        }
        
        if (isAllOrgsSelected && selectedLocations.length === 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={controlTextSx}>All Organizations</Typography>
                </Box>
            );
        }
        
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={controlTextSx}>
                    {totalSelected} {totalSelected === 1 ? 'item' : 'items'} selected
                </Typography>
            </Box>
        );
    };

    return (
        <FormControl sx={{ minWidth: 230 }}>
            <Select
                data-testid={dataTestId}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={[]}
                multiple
                displayEmpty
                renderValue={renderValue}
                sx={{
                    fontSize: '0.76rem',
                    fontWeight: 500,
                    borderRadius: '6px',
                    minHeight: controlHeight,
                    backgroundColor: '#f3f4f6',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d1d5db',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d1d5db',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d97706',
                    },
                    '& .MuiSelect-select': {
                        minHeight: 'unset !important',
                        height: `${controlHeight}px`,
                        display: 'flex',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        py: 0,
                        px: 1.25,
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 400,
                            minWidth: 320,
                        },
                    },
                }}
            >
                {/* Select All */}
                <MenuItem
                    value="select-all"
                    onClick={handleSelectAll}
                    sx={{ py: 0.5, minHeight: 'auto' }}
                >
                    <Checkbox
                        size="small"
                        checked={isAllOrgsSelected && selectedLocations.length === 0}
                        indeterminate={!isAllOrgsSelected && (selectedOrganizations.length > 0 || selectedLocations.length > 0)}
                        sx={{ py: 0, pr: 0.5 }}
                    />
                    <ListItemText
                        primary="Select All"
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                </MenuItem>

                {/* Organizations and their Locations */}
                {organizations.map((org) => (
                    <Box key={org.orgId}>
                        {/* Organization Item */}
                        <MenuItem
                            value={org.orgId}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                py: 0.4,
                                minHeight: 'auto',
                                bgcolor: expandedOrgs.has(org.orgId) ? 'action.hover' : 'transparent',
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleOrg(org.orgId);
                                }}
                                sx={{ p: 0, mr: 0.5, width: 20, height: 20 }}
                            >
                                {expandedOrgs.has(org.orgId) ? (
                                    <ExpandLess sx={{ fontSize: 16 }} />
                                ) : (
                                    <ExpandMore sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                            <Checkbox
                                size="small"
                                checked={selectedOrganizations.includes(org.orgId)}
                                indeterminate={areSomeLocationsSelected(org.orgId)}
                                onClick={(e) => handleOrgSelect(org.orgId, e)}
                                sx={{ py: 0, pr: 0.5 }}
                            />
                            <ListItemText
                                primary={org.orgName}
                                primaryTypographyProps={{ fontSize: '0.7rem', fontWeight: 500 }}
                            />
                        </MenuItem>

                        {/* Locations under the Organization */}
                        <Collapse in={expandedOrgs.has(org.orgId)} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 5 }}>
                                {org.locations && org.locations.length > 0 ? (
                                    org.locations.map((location) => (
                                        <MenuItem
                                            key={location.locationId}
                                            value={location.locationId}
                                            sx={{ py: 0.3, minHeight: 'auto' }}
                                            onClick={(e) => handleLocationSelect(org.orgId, location.locationId, e)}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={selectedLocations.includes(location.locationId)}
                                                sx={{ py: 0, pr: 0.5 }}
                                            />
                                            <ListItemText
                                                primary={location.locationName}
                                                primaryTypographyProps={{ fontSize: '0.65rem' }}
                                            />
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            No locations
                                        </Typography>
                                    </MenuItem>
                                )}
                            </Box>
                        </Collapse>
                    </Box>
                ))}
            </Select>
        </FormControl>
    );
};

export default OrganizationLocationFilter;
